import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import { Card } from '../../components/ui/card';
import axios from 'axios';
import { DollarSign, Calendar, TrendingUp } from 'lucide-react';

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/transactions`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);
  const totalCommission = transactions.reduce((sum, t) => sum + t.commission, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-foreground font-heading text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-black text-foreground tracking-tight mb-2">
            Transaction Ledger
          </h1>
          <p className="text-muted-foreground">Platform transactions and commission tracking</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Transactions</p>
                <p className="text-3xl font-bold text-foreground mt-2" data-testid="total-transactions">
                  {transactions.length}
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-sm flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" strokeWidth={1.5} />
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Amount</p>
                <p className="text-3xl font-bold text-foreground mt-2" data-testid="transaction-amount">
                  ${totalAmount.toFixed(0)}
                </p>
              </div>
              <div className="h-12 w-12 bg-emerald-100 rounded-sm flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-emerald-700" strokeWidth={1.5} />
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Commission</p>
                <p className="text-3xl font-bold text-foreground mt-2" data-testid="transaction-commission">
                  ${totalCommission.toFixed(0)}
                </p>
              </div>
              <div className="h-12 w-12 bg-amber-100 rounded-sm flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-amber-700" strokeWidth={1.5} />
              </div>
            </div>
          </Card>
        </div>

        {transactions.length === 0 ? (
          <Card className="bg-white border border-border rounded-sm shadow-sm p-12 text-center">
            <DollarSign className="h-16 w-16 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">No transactions yet</p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="transactions-list">
            {transactions.map((transaction) => (
              <Card
                key={transaction.id}
                data-testid={`transaction-${transaction.id}`}
                className="bg-white border border-border rounded-sm shadow-sm p-6 hover:shadow-md transition-shadow"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                  <div className="md:col-span-3">
                    <p className="text-sm text-muted-foreground mb-1">Transaction ID</p>
                    <p className="font-mono text-sm font-medium text-foreground">
                      {transaction.id.slice(0, 12).toUpperCase()}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">Booking ID</p>
                    <p className="font-mono text-sm font-medium text-foreground">
                      {transaction.booking_id.slice(0, 8)}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">Date</p>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                      <p className="text-sm font-medium text-foreground">
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="md:col-span-2 text-right">
                    <p className="text-sm text-muted-foreground mb-1">Amount</p>
                    <p className="font-bold text-lg text-foreground">
                      ${transaction.amount.toFixed(2)}
                    </p>
                  </div>

                  <div className="md:col-span-2 text-right">
                    <p className="text-sm text-muted-foreground mb-1">Commission (5%)</p>
                    <p className="font-bold text-lg text-accent">
                      ${transaction.commission.toFixed(2)}
                    </p>
                  </div>

                  <div className="md:col-span-1 text-right">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
                      transaction.status === 'pending' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                      transaction.status === 'completed' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                      'bg-slate-100 text-slate-600 border border-slate-200'
                    }`}>
                      {transaction.status}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTransactions;